import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Router } from '@angular/router';
import { Contact, Contract } from 'src/app/classes/models';
import { AuthService } from 'src/app/services/auth.service';
import { ContractService } from 'src/app/services/contract.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';

export type currentRouteInfo = {
  title: string;
  currentRoute:
    'siteSelection' | 'vendorSelection' | 'addContract' | 'review' | 'edit';
  nextRoute:
    | 'siteSelection'
    | 'vendorSelection'
    | 'addContract'
    | 'review'
    | 'contracts'
    | 'edit';
  previousRoute?:
    'siteSelection' | 'vendorSelection' | 'addContract' | 'review' | 'edit';
};

@Component({
  selector: 'app-contract',
  templateUrl: './contract.component.html',
  styleUrls: ['./contract.component.less'],
  providers: [SharedDataService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ContractComponent {
  CompanyId: number;
  currentRouteInfo?: currentRouteInfo;
  addingContract: boolean = false;
  viewingContract: boolean = false;
  editingContract: boolean = false;
  Contract: Contract | null = null;
  ContractId: number | null = null;
  isFormValid: boolean = false;

  currentRouteInfoMap: currentRouteInfo[] = [
    {
      title: 'Add Contract',
      currentRoute: 'addContract',
      nextRoute: 'siteSelection'
    },
    {
      title: 'Edit Contract',
      currentRoute: 'edit',
      nextRoute: 'siteSelection'
    },
    {
      title: 'Choose Site',
      currentRoute: 'siteSelection',
      nextRoute: 'vendorSelection',
      previousRoute: 'addContract'
    },
    {
      title: 'Choose Vendor',
      currentRoute: 'vendorSelection',
      nextRoute: 'review',
      previousRoute: 'siteSelection'
    },
    {
      title: 'Final Review',
      currentRoute: 'review',
      nextRoute: 'contracts',
      previousRoute: 'vendorSelection'
    }
  ];

  constructor(
    private Router: Router,
    private ToastService: ToastService,
    private AuthService: AuthService,
    private ContractService: ContractService,
    private SharedDataService: SharedDataService
  ) {
    this.CompanyId = this.AuthService.getUserCompanyId();

    const navigation = this.Router.currentNavigation();

    if (navigation && navigation.extras) {
      const { state } = navigation.extras;

      if (state) {
        const { adding, viewing, editing, ContractId } = state;

        if (adding) {
          this.setContractAction('adding');
        }

        if (viewing) {
          const reviewRoute = this.currentRouteInfoMap.find(
            (route) => route.currentRoute === 'review'
          );
          if (reviewRoute) {
            Object.assign(reviewRoute, { title: 'View Contract' });
          }

          this.setContractAction('viewing');
        }

        if (editing) {
          if (!ContractId) {
            throw this.ToastService.addErrorToast(
              'Error',
              'No Contract ID found. Cannot Edit.',
              { returnError: true }
            );
          }

          const addContractRoute = this.currentRouteInfoMap.find(
            (route) => route.currentRoute === 'addContract'
          );
          if (addContractRoute) {
            Object.assign(addContractRoute, { title: 'Edit Contract' });
          }

          this.setContractAction('editing');
          this.ContractId = ContractId;
        }
      }
    }

    this.currentRouteInfo = this.currentRouteInfoMap.find(
      (info) => info.currentRoute === this.Router.url.split('/').pop()
    );

    this.SharedDataService.listenForEvent<
      Contract | [] | number | null | undefined
    >().subscribe({
      next: ({ eventName, data }) => {
        if (eventName === 'UpdateContract' && data instanceof Contract) {
          const { id } = data;

          if (this.Contract) {
            Object.assign(this.Contract, { ...data });
          } else {
            this.Contract = { ...data };
          }

          if (id) {
            this.ContractId = id;
          }
        }

        if (eventName === 'GetContractId') {
          this.SharedDataService.sendEvent('SetContractId', this.ContractId);
        }

        if (
          eventName === 'UpdateSiteContact' &&
          data instanceof Contact &&
          this.Contract
        ) {
          Object.assign(this.Contract, { SiteContact: data });
        }

        if (eventName === 'UpdateContractFiles' && Array.isArray(data)) {
          if (!this.Contract) {
            this.Contract = new Contract({ Files: data });
          } else {
            Object.assign(this.Contract, { Files: data });
          }
        }

        if (eventName === 'formIsValid' && this.Contract) {
          if (!this.currentRouteInfo) {
            throw this.ToastService.addErrorToast(
              'Error',
              'No route information currently stored.',
              { returnError: true }
            );
          }

          const { nextRoute } = this.currentRouteInfo;

          if (!nextRoute) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Navigation encountered an error moving to next step.',
              { returnError: true }
            );
          }

          if (!this.Contract) {
            throw this.ToastService.addErrorToast(
              'Error',
              'No Contract found to save.',
              { returnError: true }
            );
          }

          if (this.currentRouteInfo.currentRoute === 'review') {
            this.Contract.isFinalized = true;
          }

          if (!this.CompanyId) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing Client ID.',
              { returnError: true }
            );
          }

          this.ContractService.saveContract(
            Object.assign(this.Contract, { CompanyId: this.CompanyId })
          ).subscribe({
            next: (Contract) => {
              this.Contract = Contract;
              this.ContractId = this.Contract.id!;
              this.resetActions();

              if (this.currentRouteInfo?.currentRoute === 'review') {
                this.Router.navigate([
                  'client',
                  this.AuthService.getUserCompanyId(),
                  'contracts'
                ]);
              } else {
                this.currentRouteInfo = this.currentRouteInfoMap.find(
                  (info) => info.currentRoute === nextRoute
                );
                this.Router.navigate([
                  'client',
                  this.AuthService.getUserCompanyId(),
                  'contract',
                  this.ContractId,
                  nextRoute
                ]);
              }
            }
          });
        }
      }
    });
  }

  setContract(id: number) {
    this.ContractService.getContract({ id }).subscribe({
      next: (Contract: Contract) => (this.Contract = Contract)
    });
  }

  setContractAction(action: 'viewing' | 'editing' | 'adding') {
    if (action === 'viewing') {
      this.viewingContract = true;
      this.addingContract = false;
      this.editingContract = false;
    }

    if (action === 'adding') {
      this.viewingContract = false;
      this.addingContract = true;
      this.editingContract = false;
    }

    if (action === 'editing') {
      this.viewingContract = false;
      this.addingContract = false;
      this.editingContract = true;
    }
  }

  resetActions() {
    this.viewingContract = false;
    this.editingContract = false;
    this.addingContract = false;
  }

  // saveContract(Contract: Contract): Observable<Contract | null> {
  //   if (!Contract) {
  //     throw this.ToastService.addErrorToast('Error', 'No Contract found to save.', { returnError: true });
  //   }

  //   this.SharedDataService.sendEvent('isFormValid');

  //   if (this.isFormValid || (this.currentRouteInfo && this.currentRouteInfo.currentRoute === 'review')) {
  //     return this.ContractService.saveContract(Object.assign(Contract, { CompanyId: this.AuthService.getUserCompanyId() }));
  //   }

  //   return of(null);
  // };

  cancel() {
    if (this.viewingContract) {
      this.viewingContract = false;
      return this.Router.navigate([
        'client',
        this.AuthService.getUserCompanyId(),
        'contracts'
      ]);
    }

    if (!this.currentRouteInfo) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No route information currently stored.',
        { returnError: true }
      );
    }

    const { previousRoute } = this.currentRouteInfo;

    if (!previousRoute) {
      this.resetActions();
      return this.Router.navigate([
        'client',
        this.AuthService.getUserCompanyId(),
        'contracts'
      ]);
    }

    this.currentRouteInfo = this.currentRouteInfoMap.find(
      (info) => info.currentRoute === previousRoute
    );

    if (previousRoute === 'addContract') {
      return this.Router.navigate(
        [
          'client',
          this.AuthService.getUserCompanyId(),
          'contract',
          previousRoute
        ],
        { state: { ContractId: this.ContractId } }
      );
    }

    return this.Router.navigate([
      'client',
      this.AuthService.getUserCompanyId(),
      'contract',
      this.ContractId,
      previousRoute
    ]);
  }

  saveAndContinue() {
    if (!this.currentRouteInfo) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No route information currently stored.',
        { returnError: true }
      );
    }

    const { nextRoute } = this.currentRouteInfo;

    if (!nextRoute) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Navigation encountered an error moving to next step.',
        { returnError: true }
      );
    }

    if (!this.Contract) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No Contract found to save.',
        { returnError: true }
      );
    }

    if (this.currentRouteInfo.currentRoute === 'review') {
      this.Contract.isFinalized = true;
    }

    this.SharedDataService.sendEvent('isFormValid');
  }

  saveAndClose() {
    if (!this.Contract) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing contract information.',
        { returnError: true }
      );
    }

    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Client ID.', {
        returnError: true
      });
    }

    this.ContractService.saveContract(
      Object.assign(this.Contract, { CompanyId: this.CompanyId })
    ).subscribe({
      next: () => {
        this.resetActions();
        this.Router.navigate([
          'client',
          this.AuthService.getUserCompanyId(),
          'contracts'
        ]);
      }
    });
  }
}
